import React from "react";
import { motion } from "framer-motion";
import { Calendar, Car } from "lucide-react";

const CarCard = ({ car, onPrebook }) => {

  // Button rendering logic
  const renderButton = () => {
    if (
      (car.prebook && car.available) ||
      (car.limited && car.prebook && car.available)
    ) {
      return (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="btn btn-luxury w-100 py-3 fw-bold"
          onClick={() => onPrebook(car)}
          style={{
            borderRadius: "0",
            fontSize: "0.8rem",
            letterSpacing: "2px"
          }}
        >
          PRE-ORDER
        </motion.button>
      );
    }

    if (car.limited && car.launched && (!car.prebook || !car.available)) {
      return (
        <button
          className="btn w-100 py-3 fw-bold"
          disabled
          style={{
            background: "var(--bg-surface)",
            color: "var(--text-muted)",
            fontFamily: "var(--font-body)",
            cursor: "not-allowed",
            border: "1px solid var(--glass-border)",
            borderRadius: "0",
            fontSize: "0.8rem",
            letterSpacing: "2px",
            textTransform: "uppercase"
          }}
        >
          Sold Out
        </button>
      );
    }
    if (car.available && (!car.launched) && (!car.prebook)) {
      return (
        <button
          className="btn w-100 py-3 fw-bold"
          disabled
          style={{
            background: "FFFFFF",
            color: "var(--highlight-gold)",
            fontFamily: "var(--font-body)",
            cursor: "not-allowed",
            border: "1px solid var(--glass-border)",
            borderRadius: "0",
            fontSize: "0.8rem",
            letterSpacing: "2px",
            textTransform: "uppercase"
          }}
        >
          Coming Soon...
        </button>
      );
    }
    // fallback
    return (
      <button
        className="btn w-100 py-3 fw-bold"
        disabled
        style={{
          background: "var(--bg-surface)",
          color: "var(--text-muted)",
          fontFamily: "var(--font-body)",
          cursor: "not-allowed",
          border: "none",
          borderRadius: "0",
          fontSize: "0.8rem",
          letterSpacing: "2px",
          textTransform: "uppercase"
        }}
      >
        Not Available
      </button>
    );
  };

  return (
    <motion.div
      className="h-100 glass-panel d-flex flex-column"
      whileHover={{ y: -5, borderColor: "var(--primary-accent)" }}
      transition={{ duration: 0.3 }}
      style={{
        borderRadius: "4px",
        overflow: "hidden",
        border: "1px solid var(--glass-border)",
        background: "var(--bg-card)",
        opacity: car.available ? 1 : 0.7, // Muted opacity for sold out
        filter: car.available ? "none" : "grayscale(0.4)" // Slight grayscale for sold out container
      }}
    >
      <div className="position-relative overflow-hidden">
        <motion.img
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.5 }}
          src={car.image}
          alt={car.name}
          className="w-100"
          style={{
            height: "250px",
            objectFit: "cover",
            filter: car.available
              ? "grayscale(20%) contrast(1.1)"
              : "grayscale(90%) contrast(0.9)" // Heavy grayscale for sold out image
          }}
        />

        <div className="position-absolute top-0 start-0 m-3">
          {!car.launched && (
            <span
              className="badge"
              style={{
                background: "rgba(15, 17, 21, 0.8)",
                backdropFilter: "blur(4px)",
                color: "var(--highlight-gold)",
                fontWeight: "600",
                borderRadius: "0",
                padding: "8px 12px",
                border: "1px solid var(--glass-border)",
                textTransform: "uppercase",
                letterSpacing: "1px",
                fontSize: "0.7rem"
              }}
            >
              Coming Soon
            </span>
          )}
        </div>

        <div className="position-absolute top-0 end-0 m-3">
          <span
            className="badge"
            style={{
              background: car.available
                ? "rgba(15, 17, 21, 0.8)"
                : "rgba(15, 17, 21, 0.8)",
              backdropFilter: "blur(4px)",
              color: car.available ? "var(--highlight-gold)" : "var(--text-muted)",
              fontWeight: "600",
              borderRadius: "0",
              padding: "8px 12px",
              border: "1px solid var(--glass-border)",
              textTransform: "uppercase",
              letterSpacing: "1px",
              fontSize: "0.7rem"
            }}
          >
            {car.available ? "Available" : "Sold Out"}
          </span>
        </div>
      </div>

      <div className="p-4 d-flex flex-column flex-grow-1">
        <div className="mb-3">
          <h3
            className="h5 fw-bold mb-0 text-uppercase"
            style={{
              color: "var(--text-main)",
              fontFamily: "var(--font-heading)",
              letterSpacing: "1px",
              lineHeight: "1.4"
            }}
          >
            {car.name}
          </h3>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4 border-top border-bottom py-2" style={{ borderColor: "var(--glass-border)" }}>
          <div className="d-flex gap-3">
            <div className="d-flex align-items-center text-white" style={{ fontSize: "0.85rem" }}>
              <Calendar size={14} className="me-2" style={{ color: "var(--secondary-accent)" }} />
              <span style={{ fontFamily: "var(--font-body)" }}>{car.year}</span>
            </div>
            <div className="d-flex align-items-center text-white" style={{ fontSize: "0.85rem" }}>
              <Car size={14} className="me-2" style={{ color: "var(--secondary-accent)" }} />
              <span style={{ fontFamily: "var(--font-body)" }}>{car.cars}</span>
            </div>
          </div>
          <div
            className="fw-bold"
            style={{
              color: "var(--primary-accent)",
              fontFamily: "var(--font-body)",
              fontSize: "1.1rem"
            }}
          >
            {car.price}
          </div>
        </div>

        <p
          className="mb-4 flex-grow-1"
          style={{
            color: "var(--text-muted)",
            fontSize: "0.9rem",
            lineHeight: "1.6",
            fontWeight: "300"
          }}
        >
          {car.description}
        </p>

        <div className="mt-auto">
          {renderButton()}
        </div>
      </div>
    </motion.div >
  );
};

export default CarCard;
